import { RefreshCw01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { useTrackingStats, useRefetchTrackingStats } from '@/hooks/api/use-tracking-stats';

export const ApiUsageTab = () => {
    const { data, isLoading, isError, error } = useTrackingStats();
    const refetchStats = useRefetchTrackingStats();

    const stats = data?.data;

    // Calculate percentage used
    const percentageUsed = stats
        ? Math.round((stats.requests_made / stats.requests_total) * 100)
        : 0;

    // Calculate remaining percentage for progress bar
    const percentageRemaining = 100 - percentageUsed;

    // Determine color based on usage
    const getProgressColor = () => {
        if (percentageUsed >= 90) return 'bg-error-500';
        if (percentageUsed >= 70) return 'bg-warning-500';
        return 'bg-brand-500';
    };

    const getTextColor = () => {
        if (percentageUsed >= 90) return 'text-error-600';
        if (percentageUsed >= 70) return 'text-warning-600';
        return 'text-brand-600';
    };

    if (isLoading) {
        return (
            <div className="rounded-lg border border-secondary bg-primary p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-secondary rounded w-1/3"></div>
                    <div className="h-4 bg-secondary rounded w-1/2"></div>
                    <div className="h-8 bg-secondary rounded w-full"></div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-lg border border-error-200 bg-error-50 p-6">
                <h3 className="text-lg font-semibold text-error-700 mb-2">
                    Failed to load API statistics
                </h3>
                <p className="text-error-600 text-sm mb-4">
                    {error instanceof Error ? error.message : 'An unexpected error occurred'}
                </p>
                <Button
                    color="secondary"
                    onClick={() => refetchStats()}
                    iconLeading={RefreshCw01}
                    size="sm"
                >
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* JSONCargo API Usage Card */}
            <div className="rounded-lg border border-secondary bg-primary p-6">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-semibold text-primary">
                            JSONCargo API Usage
                        </h3>
                        <p className="text-tertiary text-sm mt-1">
                            Track container tracking API consumption
                        </p>
                    </div>
                    <Button
                        color="secondary"
                        onClick={() => refetchStats()}
                        iconLeading={RefreshCw01}
                        size="sm"
                    >
                        Refresh
                    </Button>
                </div>

                {stats && (
                    <div className="space-y-6">
                        {/* Plan Badge */}
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-tertiary">Current Plan:</span>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-brand-100 text-brand-700">
                                {stats.plan}
                            </span>
                        </div>

                        {/* Progress Section */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-primary">API Calls Used</span>
                                <span className="text-sm text-tertiary">
                                    {stats.requests_made.toLocaleString()} / {stats.requests_total.toLocaleString()}
                                </span>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all duration-300 ${getProgressColor()}`}
                                    style={{ width: `${percentageUsed}%` }}
                                />
                            </div>

                            {/* Usage Stats */}
                            <div className="flex items-center justify-between text-sm">
                                <span className={`font-medium ${getTextColor()}`}>
                                    {percentageUsed}% used
                                </span>
                                <span className="text-tertiary">
                                    {percentageRemaining}% remaining
                                </span>
                            </div>
                        </div>

                        {/* Remaining Calls Card */}
                        <div className="bg-secondary rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-tertiary">Remaining Calls</p>
                                    <p className="text-2xl font-semibold text-primary mt-1">
                                        {stats.requests_available.toLocaleString()}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-tertiary">Total Allocation</p>
                                    <p className="text-lg font-medium text-primary mt-1">
                                        {stats.requests_total.toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Warning if usage is high */}
                        {percentageUsed >= 80 && (
                            <div className={`rounded-lg p-4 ${percentageUsed >= 90 ? 'bg-error-50 border border-error-200' : 'bg-warning-50 border border-warning-200'}`}>
                                <p className={`text-sm font-medium ${percentageUsed >= 90 ? 'text-error-700' : 'text-warning-700'}`}>
                                    {percentageUsed >= 90
                                        ? 'Critical: API usage is above 90%. Consider upgrading your plan.'
                                        : 'Warning: API usage is above 80%. Monitor your consumption closely.'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
